import { SlidersHorizontal } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { DateRowInput } from "@/components/ui/date-row-input";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import {
  activeDealFilterCount,
  EMPTY_DEAL_FILTERS,
  type DealAdvancedFilterState,
} from "./dealFilters";

export interface DealAdvancedFiltersProps {
  value: DealAdvancedFilterState;
  onChange: (next: DealAdvancedFilterState) => void;
  /** Manager ledger only: partner-org select options (from loaded rows). */
  orgOptions?: string[];
  /** Manager ledger only: owner (lead BDM/PO) select options. */
  ownerOptions?: string[];
}

const SELECT_CLASS =
  "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm transition-colors focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50";

/**
 * Advanced Filters (CHP-330 / CHP-315, shared by the partner + manager
 * ledgers): an "Advanced Filters" toggle with an active-group count badge,
 * expanding an inline panel of submitted-date-range + deal-value controls
 * (+ org & owner selects on the manager ledger). Status filtering is not here:
 * CHP-364 replaced the green per-status chips with the renamed tabs + the
 * clickable Status-column grouping. The panel closes on click-outside.
 * Pure-state component — the host owns the filter state and applies
 * `matchesDealFilters` over its already-fetched rows.
 */
export function DealAdvancedFilters({
  value,
  onChange,
  orgOptions,
  ownerOptions,
}: DealAdvancedFiltersProps) {
  const [open, setOpen] = React.useState(false);
  const count = activeDealFilterCount(value);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Close the panel when the user clicks anywhere outside it (CHP-330).
  React.useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  function set<K extends keyof DealAdvancedFilterState>(
    key: K,
    fieldValue: DealAdvancedFilterState[K],
  ) {
    onChange({ ...value, [key]: fieldValue });
  }

  return (
    <div className="relative" ref={containerRef}>
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid="deal-filters-toggle"
      >
        <SlidersHorizontal className="h-4 w-4" aria-hidden />
        Advanced Filters
        {count > 0 ? (
          <span
            className="ml-1 rounded-full bg-primary px-1.5 text-xs font-semibold text-primary-foreground tabular-nums"
            data-testid="deal-filters-count"
          >
            {count}
          </span>
        ) : null}
      </Button>

      {open ? (
        <div
          // CHP-870: clamp the panel width to the viewport (minus a 2rem
          // gutter) so it can never overflow horizontally and push the layout /
          // left nav out of place on a short ledger. Caps at 28rem on normal
          // widths; the right-0 anchor keeps it under the toggle.
          className="absolute right-0 z-20 mt-2 w-[min(28rem,calc(100vw-2rem))] max-w-[calc(100vw-2rem)] space-y-4 rounded-md border bg-popover p-4 shadow-md"
          data-testid="deal-filters-panel"
        >
          <fieldset>
            <legend className="mb-2 text-sm font-semibold">Submitted between</legend>
            {/* flex-wrap keeps the two date inputs from overflowing the panel
                on narrow widths (CHP-330 date-section overflow fix). */}
            <div className="flex flex-wrap items-center gap-2">
              <Label htmlFor="deal-filter-submitted-from" className="sr-only">
                Submitted from
              </Label>
              {/* CHP-396: whole-row click opens the picker. */}
              <DateRowInput
                id="deal-filter-submitted-from"
                value={value.submittedFrom}
                onChange={(e) => set("submittedFrom", e.target.value)}
                aria-label="Submitted from"
                wrapperClassName="w-auto min-w-0 flex-1"
              />
              <span className="text-sm text-muted-foreground">to</span>
              <DateRowInput
                value={value.submittedTo}
                onChange={(e) => set("submittedTo", e.target.value)}
                aria-label="Submitted to"
                wrapperClassName="w-auto min-w-0 flex-1"
              />
            </div>
          </fieldset>

          <fieldset>
            <legend className="mb-2 text-sm font-semibold">Closing between</legend>
            {/* CHP-370: the "Total Sales Closed YTD" scorecard applies a
                current-year close-date range to this group; exposing the inputs
                keeps that active filter visible and editable. The predicate
                resolves closing date to closed_at for terminal deals. */}
            <div className="flex flex-wrap items-center gap-2">
              <Label htmlFor="deal-filter-closing-from" className="sr-only">
                Closing from
              </Label>
              <DateRowInput
                id="deal-filter-closing-from"
                value={value.closingFrom}
                onChange={(e) => set("closingFrom", e.target.value)}
                aria-label="Closing from"
                wrapperClassName="w-auto min-w-0 flex-1"
              />
              <span className="text-sm text-muted-foreground">to</span>
              <DateRowInput
                value={value.closingTo}
                onChange={(e) => set("closingTo", e.target.value)}
                aria-label="Closing to"
                wrapperClassName="w-auto min-w-0 flex-1"
              />
            </div>
          </fieldset>

          <fieldset>
            <legend className="mb-2 text-sm font-semibold">Deal value (AUD)</legend>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                type="number"
                min={0}
                placeholder="Min"
                value={value.valueMin}
                onChange={(e) => set("valueMin", e.target.value)}
                aria-label="Minimum deal value"
                className="w-auto min-w-0 flex-1"
              />
              <span className="text-sm text-muted-foreground">to</span>
              <Input
                type="number"
                min={0}
                placeholder="Max"
                value={value.valueMax}
                onChange={(e) => set("valueMax", e.target.value)}
                aria-label="Maximum deal value"
                className="w-auto min-w-0 flex-1"
              />
            </div>
          </fieldset>

          {orgOptions || ownerOptions ? (
            <div className="grid gap-4 sm:grid-cols-2">
              {orgOptions ? (
                <div>
                  <Label htmlFor="deal-filter-org" className="mb-2 block text-sm font-semibold">
                    Partner organisation
                  </Label>
                  <select
                    id="deal-filter-org"
                    className={SELECT_CLASS}
                    value={value.partnerOrg}
                    onChange={(e) => set("partnerOrg", e.target.value)}
                  >
                    <option value="">Any</option>
                    {orgOptions.map((org) => (
                      <option key={org} value={org}>
                        {org}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              {ownerOptions ? (
                <div>
                  <Label htmlFor="deal-filter-owner" className="mb-2 block text-sm font-semibold">
                    Owner
                  </Label>
                  <select
                    id="deal-filter-owner"
                    className={SELECT_CLASS}
                    value={value.owner}
                    onChange={(e) => set("owner", e.target.value)}
                  >
                    <option value="">Any</option>
                    {ownerOptions.map((owner) => (
                      <option key={owner} value={owner}>
                        {owner}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
            </div>
          ) : null}

          <div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={count === 0}
              onClick={() => onChange(EMPTY_DEAL_FILTERS)}
              data-testid="deal-filters-clear"
            >
              Clear all
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
